import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';

import { useLikeForumPost } from '~/api/forum/useLikeForumPost';
import { useUser } from '~/api/user';
import { Toggle } from '~/components';
import { toast } from '~/utils/toast';

import { likeButtonStyle } from './LikeForumPostButton.css';

type Props = {
  likes: number[];
  postId: number;
  size?: 'normal' | 'large';
};

export const LikeForumPostButton = ({ likes, postId, size = 'normal' }: Props) => {
  const { data: user } = useUser();
  const mutation = useLikeForumPost(postId);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation(['common', 'forum']);

  const count = match(mutation.variables)
    .with(true, () => new Set([...likes, user.id]).size)
    .with(false, () => likes.filter((userId) => userId !== user.id).length)
    .with(undefined, () => likes.length)
    .exhaustive();

  const liked = mutation.variables !== undefined ? mutation.variables : likes.includes(user.id);

  return (
    <Toggle
      aria-label={t('forum:post.card.actions.like', { count })}
      className={likeButtonStyle}
      emphasis="tertiary"
      icon={liked ? 'FavoriteFill' : 'Favorite'}
      onClick={() => {
        mutation.mutate(!liked, {
          onError: () => {
            mutation.reset();
            toast.danger({ description: t('common:error') });
          },
        });

        const { peak, rebound } = liked
          ? { peak: size === 'normal' ? 90 : 95, rebound: 102 }
          : { peak: size === 'normal' ? 110 : 105, rebound: 95 };

        buttonRef.current?.animate(
          [
            { transform: 'scale(100%)', offset: 0 },
            { transform: `scale(${peak}%)`, offset: 0.3 },
            { transform: `scale(${rebound}%)`, offset: 0.75 },
            { transform: 'scale(100%)', offset: 1 },
          ],
          {
            duration: 400,
            easing: 'ease-out',
          },
        );
      }}
      ref={buttonRef}
    >
      {count}
    </Toggle>
  );
};
